import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Switch, Button, Popover } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import i18n from 'i18next';
import FileDropzoneV3 from '@src/components/FileDropzoneV3';
import DropdownCustom from '@src/new-components/Dropdown';
import Typography from '@src/new-components/Typography';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Link, useHistory } from 'react-router-dom';
import NavigationBlocker from '@src/new-components/NavigationBlocker';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useCreditsLocked from '@src/hooks/useCreditsLocked';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import LockCycle from '@src/assets/images/lock-cycle.svg';
import { uploadToS3FromFile } from '@src/services/upload';
import actions from '@src/redux/actions';
import { COLOR } from '@src/styles/color';
import {
  PRESENTATION_FILE_FORMAT,
  PRESENTATION_S3_DIRECTORY,
} from '@src/constants/presentation';
import {
  PPTX_SMALL_SLIDE_THRESHOLD,
  PROJECT_PRODUCT,
  PROJECT_TYPE,
} from '@src/constants/project';
import { trackProjectCreated } from '@src/services/moengage';
import { MOE_SURFACE } from '@src/constants/moengage';
import { hasActiveFeature } from '@src/services/package';
import useAINarrationUpgrade from '@src/hooks/useAINarrationUpgrade';
import PopupUpPkgUseRewrite from '@src/containers/ProjectDetail/Dubbing/Content/PopupUpPkgUseRewrite';
import { LANGUAGE } from '@src/constants';
import Tooltip from '@src/components/Tooltip';
import { FEATURE } from '@src/constants/package';
import CrownIcon from '@src/assets/icons/icon-crown.svg';
import route from '@src/constants/route';
import {
  getSlideCountFromPptx,
  getNoteCountFromPptx,
  calculatePresentationCredit,
} from '@src/utils/presentation';

const UploadFileByPptx = ({
  open,
  onClose,
  handleCreateProject,
  addProcessingProject,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { language: currentLang } = i18n;
  const { user } = useSelector((state) => state.auth);
  const { languagesV3 } = useSelector((state) => state.language);
  const { getFeatureValue } = useFeatureFlags();
  const [loadingGetContentFile, setLoadingGetContentFile] = useState(false);
  const [openDialogLockedCycle, setOpenDialogLockedCycle] = useState(null);
  // ON by default even without package access — the paywall fires on submit
  const [enableAIContent, setEnableAIContent] = useState(true);
  const [openPopupUpPkg, setOpenPopupUpPkg] = useState(false);
  const [contentLanguage, setContentLanguage] = useState('vi');
  const [selectedModel, setSelectedModel] = useState('');
  const [modelAnchorEl, setModelAnchorEl] = useState(null);
  const [slideCount, setSlideCount] = useState(0);
  const [noteCount, setNoteCount] = useState(0);
  const dispatch = useDispatch();
  const isLeavingRef = useRef(false);

  useEffect(() => {
    if (open) {
      isLeavingRef.current = false;
      if (!languagesV3?.length) {
        dispatch(actions.language.getLanguagesV3());
      }
      return;
    }
    setOpenDialogLockedCycle(null);
    setOpenPopupUpPkg(false);
    // Back to the default (ON) so reopening matches a first open
    setEnableAIContent(true);
    setContentLanguage('vi');
    setModelAnchorEl(null);
    setSlideCount(0);
    setNoteCount(0);
  }, [open]);

  const presentationLLMEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_LLM,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );
  const presentationAIModelsConfig = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_AI_MODELS,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );
  const presentationAIModels = useMemo(() => {
    if (!Array.isArray(presentationAIModelsConfig)) {
      return [];
    }

    const validatedModels = presentationAIModelsConfig.filter(
      (model) =>
        model &&
        typeof model.value === 'string' &&
        model.description &&
        typeof model.description === 'object' &&
        (typeof model.description.en === 'string' ||
          typeof model.description.vn === 'string' ||
          typeof model.description.vi === 'string') &&
        typeof model.creditPerSlide === 'number',
    );

    return validatedModels;
  }, [presentationAIModelsConfig]);

  const getModelDescription = useCallback(
    (model) => {
      const description = model?.description || {};
      if (currentLang === LANGUAGE.EN) {
        return description.en || description.vn || description.vi || '';
      }
      return description.vn || description.vi || description.en || '';
    },
    [currentLang],
  );

  const hasPresentationAIModels = presentationAIModels.length > 0;

  useEffect(() => {
    if (!presentationAIModels.length) {
      setSelectedModel('');
      return;
    }
    const hasSelectedModel = presentationAIModels.some(
      (model) => model.value === selectedModel,
    );
    if (!hasSelectedModel) {
      setSelectedModel(presentationAIModels[0].value);
    }
  }, [presentationAIModels, selectedModel]);

  const hasAIPresentationAccess = hasActiveFeature({
    user,
    feature: FEATURE.AI_GENERATE_NARRATION,
  });

  const { isCreditsLocked } = useCreditsLocked();
  const { upgradePkgName, targetPackageCode } = useAINarrationUpgrade();

  const isAIContentEnabled =
    hasAIPresentationAccess && hasPresentationAIModels && enableAIContent;

  const handleAddFile = async (file, handleReset) => {
    // AI narration switched on without package access → upsell, keep the switch ON
    if (presentationLLMEnabled && enableAIContent && !hasAIPresentationAccess) {
      setOpenPopupUpPkg(true);
      return;
    }
    if (isAIContentEnabled && isCreditsLocked) {
      setOpenDialogLockedCycle({
        title: t('charactersTemporarilyLocked'),
        description: t('descriptionPopupLockedCredit'),
      });
      return;
    }
    setLoadingGetContentFile(true);
    try {
      const fileUrl = await uploadToS3FromFile(file, PRESENTATION_S3_DIRECTORY);
      const title = t('untitledTitleProject', {
        time: moment().format('YYYYMMDD-HH:mm'),
      });
      const source = {
        url: fileUrl,
        type: PROJECT_TYPE.PRESENTATION,
      };

      if (isAIContentEnabled) {
        source.language = contentLanguage;
        source.model = selectedModel;
      }

      const res = await handleCreateProject({
        title,
        product: 'PRESENTATION',
        isDeleted: false,
        blocks: [],
        source,
      });
      if (res.status) {
        const projectId = res?.result?.project?.id;
        trackProjectCreated({
          product: PROJECT_PRODUCT.PRESENTATION,
          source,
        });
        const isLargeFile = slideCount >= PPTX_SMALL_SLIDE_THRESHOLD;
        addProcessingProject?.(projectId, isLargeFile);
        if (!isLeavingRef.current) {
          setLoadingGetContentFile(false);
          dispatch(actions.project.setProduct(PROJECT_PRODUCT.PRESENTATION));
          onClose();
          history.push(`${route.PROJECTS}/${projectId}`);
        }
      }
    } catch (err) {
      dispatch(
        actions.noti.push({ severity: 'error', message: 'uploadError' }),
      );
      handleReset?.();
    } finally {
      setLoadingGetContentFile(false);
    }
  };

  const handleFileChange = useCallback(async (file) => {
    if (file) {
      const [slides, notes] = await Promise.all([
        getSlideCountFromPptx(file),
        getNoteCountFromPptx(file),
      ]);
      setSlideCount(slides);
      setNoteCount(notes);
    } else {
      setSlideCount(0);
      setNoteCount(0);
    }
  }, []);

  useEffect(() => {
    if (!loadingGetContentFile) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [loadingGetContentFile]);

  const selectedModelData = presentationAIModels.find(
    (m) => m.value === selectedModel,
  );
  const creditCost = calculatePresentationCredit({
    enableAI: isAIContentEnabled,
    slideCount,
    model: selectedModelData,
  });

  const fileDetails = slideCount
    ? [
        t('slideCount', { count: slideCount }),
        t('noteCount', { count: noteCount }),
      ]
    : [];

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const upgradeTooltipTitle = (
    <span>
      <span
        // eslint-disable-next-line
        dangerouslySetInnerHTML={{
          __html: t('descriptionUnlockPremiumAIFeaturesNow', {
            pkgName: upgradePkgName,
          }),
        }}
      />{' '}
      <Link
        to={route.PAYMENT}
        style={{ color: '#fff', textDecoration: 'underline' }}
      >
        {t('buyNow')}
      </Link>
    </span>
  );

  const languageOptions = useMemo(
    () =>
      [...(languagesV3 || [])]
        .filter((item) => item.value !== 'multilingual')
        .sort((a, b) => {
          if (a.rank !== b.rank) return a.rank - b.rank;
          if (currentLang === LANGUAGE.VN) {
            return (a.label?.vi ?? '').localeCompare(b.label?.vi ?? '');
          }
          return (a.label?.en ?? '').localeCompare(b.label?.en ?? '');
        })
        .map((item) => ({
          ...item,
          label: item.label?.[currentLang] || item.label?.[LANGUAGE.EN],
        })),
    [languagesV3, currentLang],
  );

  const getSelectedLabel = (value) => {
    const option = languageOptions.find((item) => item.value === value);
    return option?.label || '';
  };

  const dropdownWidth = useMemo(() => {
    const maxLen = Math.max(
      ...languageOptions.map((o) => (o.label || '').length),
      0,
    );
    return `${Math.max(160, Math.ceil(maxLen * 7.5) + 50)}px`;
  }, [languageOptions]);

  const aiToggleContent = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
        marginTop: '16px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              fontSize: '16px',
              fontWeight: 600,
              lineHeight: '24px',
              letterSpacing: '-0.4px',
              color: '#1c1c1e',
            }}
          >
            {t('createAIPresentationContent')}
          </span>
          <Tooltip
            placement="top"
            title={t('createAIPresentationContentTooltip')}
            arrow
          >
            <InfoOutlinedIcon
              sx={{
                width: 20,
                height: 20,
                color: 'rgba(28, 28, 30, 0.64)',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {!hasAIPresentationAccess && (
            <Tooltip placement="top" title={upgradeTooltipTitle} arrow>
              <img
                src={CrownIcon}
                alt="crown"
                style={{ width: 20, height: 20, cursor: 'pointer' }}
              />
            </Tooltip>
          )}
          <Switch
            checked={enableAIContent}
            onChange={(e) => setEnableAIContent(e.target.checked)}
            sx={{
              width: 40,
              height: 24,
              padding: 0,
              '& .MuiSwitch-switchBase': {
                padding: '2px',
                '&.Mui-checked': {
                  transform: 'translateX(16px)',
                  color: '#fff',
                  '& + .MuiSwitch-track': {
                    backgroundColor: COLOR.indigo.default,
                    opacity: 1,
                  },
                },
                '&.Mui-disabled': {
                  '& + .MuiSwitch-track': {
                    opacity: 1,
                  },
                },
              },
              '& .MuiSwitch-thumb': {
                width: 20,
                height: 20,
              },
              '& .MuiSwitch-track': {
                borderRadius: 100,
                opacity: 1,
                backgroundColor: 'rgba(120, 120, 128, 0.16)',
              },
            }}
          />
        </div>
      </div>
      {enableAIContent && (
        <>
          {hasPresentationAIModels && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Typography
                variant="body-14-medium"
                style={{ letterSpacing: '-0.4px', color: '#1c1c1e' }}
              >
                {t('selectModel')}
              </Typography>
              <div>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => setModelAnchorEl(e.currentTarget)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    height: '40px',
                    width: dropdownWidth,
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: `0.5px solid ${COLOR.indigo[16]}`,
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                  }}
                >
                  <Typography variant="body-14-regular" color={COLOR.darkGray}>
                    {selectedModel}
                  </Typography>
                  <ArrowDropDownRoundedIcon
                    sx={{ width: 20, height: 20, color: COLOR.navyBlue }}
                  />
                </div>
                <Popover
                  open={Boolean(modelAnchorEl)}
                  anchorEl={modelAnchorEl}
                  width={dropdownWidth}
                  onClose={() => setModelAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    style: { borderRadius: '8px', width: '318px' },
                    sx: {
                      marginTop: '8px',
                      boxShadow: '0px 4px 16px 0px rgba(0,0,0,0.08) !important',
                    },
                  }}
                >
                  <div style={{ padding: '8px' }}>
                    {presentationAIModels.map((model) => (
                      <div
                        key={model.value}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setSelectedModel(model.value);
                          setModelAnchorEl(null);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor:
                            selectedModel === model.value
                              ? 'rgba(30, 22, 78, 0.04)'
                              : 'transparent',
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                          }}
                        >
                          <Typography variant="body-14-medium" color="#1c1c1e">
                            {model.value}
                          </Typography>
                          <Typography
                            variant="body-12-regular"
                            color="rgba(28, 28, 30, 0.64)"
                          >
                            {getModelDescription(model)}
                          </Typography>
                          <Typography
                            variant="body-12-regular"
                            color="rgba(28, 28, 30, 0.64)"
                            style={{ fontStyle: 'italic' }}
                          >
                            {t('creditPerSlide', {
                              value: model.creditPerSlide,
                            })}
                          </Typography>
                        </div>
                        <CheckRoundedIcon
                          sx={{
                            width: 20,
                            height: 20,
                            color: COLOR.navyBlue,
                            opacity: selectedModel === model.value ? 1 : 0,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </Popover>
              </div>
            </div>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography
              variant="body-14-medium"
              style={{ letterSpacing: '-0.4px', color: '#1c1c1e' }}
            >
              {t('selectOutputLanguage')}
            </Typography>
            <DropdownCustom
              variant="form-select-light"
              width={dropdownWidth}
              options={languageOptions}
              onChange={(value) => setContentLanguage(value)}
              value={contentLanguage}
              searchable
              searchPlaceholder={t('search')}
              title={
                <Typography
                  variant="body-14-regular"
                  color={contentLanguage ? COLOR.darkGray : COLOR.label2[48]}
                >
                  {getSelectedLabel(contentLanguage) || t('selectLanguage')}
                </Typography>
              }
            />
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      {!!openDialogLockedCycle?.title && (
        <NotificationDialogV4
          image={LockCycle}
          title={openDialogLockedCycle?.title}
          description={openDialogLockedCycle?.description}
          actionComponent={
            <Button
              variant="contained"
              onClick={() => setOpenDialogLockedCycle(null)}
            >
              {t('understoodV3')}
            </Button>
          }
          open
          onClose={() => setOpenDialogLockedCycle(null)}
        />
      )}
      <PopupUpPkgUseRewrite
        open={openPopupUpPkg}
        onClose={() => setOpenPopupUpPkg(false)}
        pkgName={upgradePkgName}
        targetPackageCode={targetPackageCode}
        featureKey={FEATURE.AI_GENERATE_NARRATION}
        surface={MOE_SURFACE.PROJECT_CREATE}
        product={PROJECT_PRODUCT.PRESENTATION}
      />
      <NavigationBlocker
        when={loadingGetContentFile}
        navigate={(path) => history.push(path)}
        description={t('leaveWhileProcessingDescriptionV2')}
        onLeave={() => {
          isLeavingRef.current = true;
        }}
      />
      <CommonDialog
        open={open}
        onClose={handleClose}
        title={t('dubbedSlider')}
        disableAutoClose
        disableClose={loadingGetContentFile}
        width="640px"
        contentComponent={
          <FileDropzoneV3
            fileType={PRESENTATION_FILE_FORMAT}
            onAddFile={handleAddFile}
            onChangeFile={handleFileChange}
            loadingGetContentFile={loadingGetContentFile}
            dataId="import-via-file"
            extraFileDetails={fileDetails}
            extraContent={presentationLLMEnabled ? aiToggleContent : null}
            submitTooltip={t('costsCredit', {
              value: creditCost?.toLocaleString(currentLang),
            })}
          />
        }
      />
    </>
  );
};

export default UploadFileByPptx;
